import * as React from 'react';
import {
  getGetContentHubAssetQueryKey, getGetSocialPostQueryKey, getListContentHubAssetsQueryKey, getListSocialPostsQueryKey,
  useGetContentHubAsset, useGetSocialPost, useListContentHubAssets, useListSocialPosts, type ContentHubAssetKind
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ContentPublicationJourney } from '@/components/content-hub/content-publication-journey';
import { contentAssetDestination } from '@/components/content-hub/content-journey-destination';
import { useNativeListState } from '@/components/interaction/interaction-scope';
import { ListPagination, QueryFeedback } from '@/components/interaction/query-feedback';
import { usePageCorrection } from '@/components/content-hub/native-list';

const KINDS: ContentHubAssetKind[] = ['lead_magnet', 'testimonial', 'case_study', 'faq', 'operation'];
interface AgentContentJourneyProps {
  actionId: string;
  disabled: boolean;
  onContinue: (destination: string) => void;
}

/** Deliberate native selection only; no publication authority is inferred from a continuation. */
export function AgentContentJourney({ actionId, disabled, onContinue }: AgentContentJourneyProps) {
  const [open, setOpen] = React.useState(false);
  return <div className="mt-2 min-w-0">
    <Button size="sm" variant="outline" disabled={disabled} aria-expanded={open} onClick={() => setOpen(value => !value)}>
      {open ? 'Hide content journey' : 'Show content journey'}
    </Button>
    {open && !disabled && <ContentPicker key={actionId} actionId={actionId} onContinue={onContinue} />}
  </div>;
}

function ContentPicker({ actionId, onContinue }: Omit<AgentContentJourneyProps, 'disabled'>) {
  const labelId = React.useId();
  const [state, update] = useNativeListState('agent-content-journey:' + actionId, { search: '', filters: {}, sort: '', offset: 0, pageSize: 10 });
  const kind = KINDS.find(item => item === state.filters.kind);
  const source = kind ?? 'post';
  const params = { limit: state.pageSize, offset: state.offset };
  const posts = useListSocialPosts(params, { query: { queryKey: getListSocialPostsQueryKey(params), enabled: !kind, retry: false } });
  const assetParams = { ...params, kind };
  const assets = useListContentHubAssets(assetParams, { query: { queryKey: getListContentHubAssetsQueryKey(assetParams), enabled: Boolean(kind), retry: false } });
  const results = kind ? assets : posts;
  usePageCorrection(results, state.offset, state.pageSize, offset => update({ offset }));
  return <section aria-label="Choose content journey" className="mt-3 min-w-0 space-y-3">
    <div className="space-y-1.5"><Label htmlFor={labelId}>Content type</Label>
      <select id={labelId} className="h-9 w-full rounded-md border bg-background px-3 text-sm" value={source}
        onChange={event => update({ filters: { kind: event.target.value }, selectedId: undefined }, true)}>
        <option value="post">Social posts</option>{KINDS.map(item => <option key={item} value={item}>{item.replaceAll('_', ' ')}</option>)}
      </select>
    </div>
    {state.selectedId ? <SelectedContent key={source + ':' + state.selectedId} id={state.selectedId} kind={kind}
      onContinue={onContinue} onBack={() => update({ selectedId: undefined })} />
      : results.isLoading ? <QueryFeedback state="loading" title="Loading content…" />
      : results.isError ? <QueryFeedback state="error" title="Content is unavailable." description="Your selection has been kept." onRetry={() => { void results.refetch(); }} />
      : results.data ? <>
        {results.data.items.length ? <ul className="space-y-2">{results.data.items.map(item => <li key={item.id}>
          <Button className="h-auto w-full justify-start whitespace-normal break-words text-left" size="sm" variant="outline" onClick={() => update({ selectedId: item.id })}>
            {item.title} · {item.status?.replaceAll('_', ' ')} · v{item.version}
          </Button>
        </li>)}</ul> : <p className="text-sm">No saved content of this type.</p>}
        <ListPagination label="Content journey pages" offset={state.offset} pageSize={state.pageSize} total={results.data.total}
          pending={results.isFetching} onChange={offset => update({ offset })} />
      </> : null}
  </section>;
}

function SelectedContent({ id, kind, onContinue, onBack }: {
  id: string; kind?: ContentHubAssetKind; onContinue: (destination: string) => void; onBack: () => void;
}) {
  const post = useGetSocialPost(id, { query: { queryKey: getGetSocialPostQueryKey(id), enabled: !kind, retry: false } });
  const asset = useGetContentHubAsset(id, { query: { queryKey: getGetContentHubAssetQueryKey(id), enabled: Boolean(kind), retry: false } });
  const query = kind ? asset : post;
  const source = kind ? asset.data?.kind === kind ? { kind: 'asset' as const, record: asset.data } : null
    : post.data ? { kind: 'post' as const, record: post.data } : null;
  return <div className="min-w-0 space-y-3">
    <Button size="sm" variant="ghost" onClick={onBack}>Choose another content record</Button>
    {query.isError ? <QueryFeedback state="error" title="This content is unavailable." description="It may have changed or you may no longer have access." onRetry={() => { void query.refetch(); }} />
      : query.isLoading ? <QueryFeedback state="loading" title="Loading saved content…" />
      : source ? <>
        <ContentPublicationJourney source={source} />
        <Button size="sm" onClick={() => onContinue(source.kind === 'post' ? '/content-hub/posts/' + encodeURIComponent(id) : contentAssetDestination(source.record))}>Continue this content on screen</Button>
        <p className="text-xs text-muted-foreground">Resume the exact saved record. Use its editor to correct content, inspect evidence and explicitly review delivery.</p>
      </> : <QueryFeedback state="error" title="This content does not match the selected type." />}
  </div>;
}
